import { Badge, Button, Dropdown, Input, Menu, Popover, Select, Space, Table, Tooltip } from "antd";
import { EditOutlined, EyeOutlined, FilterOutlined } from "@ant-design/icons";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GetTotalCustomers from "../../api/getTotalCustomers";
import { getCustomerSummary } from "../account/account.api";
import { loadAllCustomer, loadAllCustomersForSearch } from "../../redux/actions/customer/getCustomerAction";
import { getCustomerBalanceParts } from "../../utils/partyBalanceLabels";
import { getPartyListBalance } from "../../utils/partyListBalance";
import "./customer.css";

const FILTER_STYLE = { marginBottom: 12 };
const FILTER_LABEL = { display: "block", marginBottom: 4, color: "rgba(0,0,0,0.65)", fontSize: 12 };

const CustomerBalanceDisplay = ({ value }) => {
  const parts = getCustomerBalanceParts(value);
  if (!parts) return <span>—</span>;
  return (
    <div className="party-balance-cell-inner">
      <span className="party-balance-amt">{parts.primary}</span>
      <div className="party-balance-label">{parts.label}</div>
    </div>
  );
};

function CustomTable({
  list,
  total,
  status,
  columnsToShow,
  setColumnsToShow,
  colVisibilityClickHandler,
  columnItems,
  isSearching,
  filteredList,
  resolvePartyBalance,
}) {
  const dispatch = useDispatch();
  const addKeys = (arr) => arr.map((i) => ({ ...i, key: i.id }));
  const dataSource = isSearching ? (filteredList || []) : (list || []);
  const displayList = addKeys(dataSource);

  return (
    <div>
      {/* Desktop: table */}
      <div className="d-none d-lg-block list-page-table-container">
        <div className="list-page-table-inner">
          <div className="list-page-table-wrap list-page-table-scroll-450">
            <Table
              className="party-list-table"
              scroll={{ x: 1200, y: 450 }}
              loading={!list}
              pagination={{
                defaultPageSize: 10,
                pageSizeOptions: [10, 20, 50, 100, 200],
                showSizeChanger: true,
                total: isSearching ? filteredList.length : total,
                onChange: (page, limit) => {
                  if (!isSearching) {
                    dispatch(loadAllCustomer({ page, limit, status }));
                  }
                },
              }}
              columns={columnsToShow}
              dataSource={displayList}
            />
          </div>
        </div>
      </div>

      {/* Mobile: card layout */}
      <div className="d-lg-none customer-list-mobile-cards">
        {displayList.length > 0 ? (
          <div className="row">
            {displayList.map((record, index) => (
              <div key={record.id} className="col-12 mb-3">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                      <h6 className="card-title mb-0 flex-grow-1">
                        <Link to={`/customer/${record.id}`} style={{ textDecoration: "none", color: "#1890ff" }}>
                          {record.name || "—"}
                        </Link>
                      </h6>
                      <span className="text-muted small">#{index + 1}</span>
                    </div>
                    {record.address && (
                      <p className="mb-1 mt-2">
                        <small className="text-muted">
                          <strong>Address:</strong> {record.address}
                        </small>
                      </p>
                    )}
                    {record.phone && (
                      <p className="mb-0">
                        <small className="text-muted">
                          <strong>Phone:</strong> {record.phone}
                        </small>
                      </p>
                    )}
                    <div className="mb-0 mt-2 customer-mobile-balance-block">
                      <small className="text-muted d-block mb-1">
                        <strong>Balance</strong>
                      </small>
                      <CustomerBalanceDisplay
                        value={
                          resolvePartyBalance
                            ? resolvePartyBalance(record)
                            : record.balance ??
                              Number(record.opening_balance ?? 0) + Number(record.invoice_due_total ?? 0)
                        }
                      />
                    </div>
                    <div className="mt-2 party-list-icon-actions d-flex align-items-center gap-1">
                      <Tooltip title="View details">
                        <Link to={`/customer/${record.id}`}>
                          <Button type="text" size="small" icon={<EyeOutlined style={{ fontSize: 18 }} />} />
                        </Link>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Link to={`/customer/${record.id}/update`} state={{ data: record }}>
                          <Button type="text" size="small" icon={<EditOutlined style={{ fontSize: 18 }} />} />
                        </Link>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">No customers found</p>
          </div>
        )}
      </div>
    </div>
  );
}

const GetAllCust = ({ refreshTrigger, addCustomerButton, uploadButton }) => {
  const dispatch = useDispatch();
  const list = useSelector((state) => state.customers.list);
  const searchList = useSelector((state) => state.customers.searchList);
  /** When list API omits balance fields, merge total_due from customer-summary (same source as reports). */
  const summaryDueByCustomerIdRef = useRef(new Map());
  const [, setSummaryMergeTick] = useState(0);
  const [total, setTotal] = useState(0);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({ name: "", phone: "", address: "" });
  const [filteredList, setFilteredList] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [columnItems, setColumnItems] = useState([]);
  const [columnsToShow, setColumnsToShow] = useState([]);
  const [status, setStatus] = useState("true");

  useEffect(() => {
    dispatch(loadAllCustomer({ status, page: 1, limit: 10 }));
  }, [dispatch, status, refreshTrigger]);

  useEffect(() => {
    GetTotalCustomers().then((res) => setTotal(res));
  }, [list]);

  useEffect(() => {
    dispatch(loadAllCustomersForSearch({ status }));
  }, [status, dispatch]);

  useEffect(() => {
    let cancelled = false;
    getCustomerSummary()
      .then((res) => {
        if (cancelled) return;
        const m = new Map();
        (res?.list || []).forEach((row) => {
          const id = Number(row.id);
          if (Number.isFinite(id)) m.set(id, Number(row.total_due) || 0);
        });
        summaryDueByCustomerIdRef.current = m;
        setSummaryMergeTick((t) => t + 1);
      })
      .catch(() => {
        if (cancelled) return;
        summaryDueByCustomerIdRef.current = new Map();
        setSummaryMergeTick((t) => t + 1);
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  useEffect(() => {
    const name = (filters.name || "").trim().toLowerCase();
    const phone = (filters.phone || "").trim().toLowerCase();
    const address = (filters.address || "").trim().toLowerCase();
    if (!searchList?.length || (!name && !phone && !address)) {
      setIsSearching(false);
      setFilteredList([]);
      return;
    }
    setIsSearching(true);
    const filtered = searchList.filter((customer) => {
      if (name && !(customer.name || "").toLowerCase().includes(name)) return false;
      if (phone && !(customer.phone || "").toLowerCase().includes(phone)) return false;
      if (address && !(customer.address || "").toLowerCase().includes(address)) return false;
      return true;
    });
    setFilteredList(filtered);
  }, [searchList, filters.name, filters.phone, filters.address]);
  
  // Column visibility functionality
  const colVisibilityClickHandler = (col) => {
    const ifColFound = columnsToShow.find((item) => item.key === col.key);
    if (ifColFound) {
      const filteredColumnsToShow = columnsToShow.filter(
        (item) => item.key !== col.key
      );
      setColumnsToShow(filteredColumnsToShow);
    } else {
      const foundIndex = columns.findIndex((item) => item.key === col.key);
      const foundCol = columns.find((item) => item.key === col.key);
      let updatedColumnsToShow = [...columnsToShow];
      updatedColumnsToShow.splice(foundIndex, 0, foundCol);
      setColumnsToShow(updatedColumnsToShow);
    }
  };
  
  const columns = [
    {
      title: "S.No",
      key: "sNo",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      ellipsis: true,
      className: "party-list-col-name",
      render: (name, { id }) => <Link to={`/customer/${id}`}>{name}</Link>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      responsive: ["md"],
      width: 240,
      ellipsis: false,
      className: "party-list-col-address",
      render: (text) => (
        <span className="party-list-address-text">{text && String(text).trim() ? text : "—"}</span>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 132,
      className: "party-list-col-phone",
    },
    {
      title: (
        <Tooltip
          title={
            "Positive = Receivable (customer owes you). " +
            "Negative = Advance / Credit (excess payment or opening credit). " +
            "Includes opening balance + sale invoice dues for this company."
          }
        >
          <span>Balance</span>
        </Tooltip>
      ),
      dataIndex: "balance",
      key: "balance",
      width: 160,
      align: "right",
      className: "party-list-col-balance",
      sorter: (a, b) =>
        getPartyListBalance(a, summaryDueByCustomerIdRef.current) -
        getPartyListBalance(b, summaryDueByCustomerIdRef.current),
      render: (_, record) => (
        <CustomerBalanceDisplay
          value={getPartyListBalance(record, summaryDueByCustomerIdRef.current)}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      fixed: "right",
      className: "party-list-col-action party-list-icon-actions",
      render: (_, record) => (
        <Space size={4} wrap={false}>
          <Tooltip title="View details">
            <Link to={`/customer/${record.id}`}>
              <Button type="text" size="small" icon={<EyeOutlined style={{ fontSize: 18 }} />} />
            </Link>
          </Tooltip>
          <Tooltip title="Edit">
            <Link to={`/customer/${record.id}/update`} state={{ data: record }}>
              <Button type="text" size="small" icon={<EditOutlined style={{ fontSize: 18 }} />} />
            </Link>
          </Tooltip>
        </Space>
      ),
    },
  ];
  
  const menuItems = columns.map((item) => {
    return {
      key: item.key,
      label: <span>{item.title}</span>,
    };
  });
  
  useEffect(() => {
    setColumnItems(menuItems);
    setColumnsToShow(columns);
  }, []);
  
  const activeFilterCount = [filters.name, filters.phone, filters.address].filter(Boolean).length;

  const filterContent = (
    <div style={{ width: 280 }}>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Name</label>
        <Input
          placeholder="Filter by name"
          value={filters.name}
          onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
          allowClear
        />
      </div>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Phone</label>
        <Input
          placeholder="Filter by phone"
          value={filters.phone}
          onChange={(e) => setFilters((f) => ({ ...f, phone: e.target.value }))}
          allowClear
        />
      </div>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Address</label>
        <Input
          placeholder="Filter by address"
          value={filters.address}
          onChange={(e) => setFilters((f) => ({ ...f, address: e.target.value }))}
          allowClear
        />
      </div>
      <div style={FILTER_STYLE}>
        <label style={FILTER_LABEL}>Status</label>
        <Select
          placeholder="Status"
          style={{ width: "100%" }}
          value={status}
          onChange={(value) => {
            setStatus(value);
            dispatch(loadAllCustomer({ status: value, page: 1, limit: 10 }));
          }}
        >
          <Select.Option value="true">Active</Select.Option>
          <Select.Option value="false">Inactive</Select.Option>
        </Select>
      </div>
      <div style={{ textAlign: "center", marginTop: 8 }}>
        <Button size="small" onClick={() => setFilters({ name: "", phone: "", address: "" })}>
          Clear All Filters
        </Button>
      </div>
    </div>
  );

  return (
    <div className="card column-design list-page-fill-viewport customer-list-page">
      <div className="card-body list-page-card-body">
        <div className="list-page-sticky-top">
          <div className="card-title d-flex justify-content-between align-items-center list-page-header" style={{ marginBottom: 0 }}>
            <h5 className="mb-0">Customer List</h5>
            <div className="customer-header-actions" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {list && (
                <>
                  <Popover
                    content={filterContent}
                    title="Filters"
                    trigger="click"
                    open={filterVisible}
                    onOpenChange={setFilterVisible}
                    placement="bottomLeft"
                  >
                    <Badge count={activeFilterCount} size="small" offset={[-2, 2]}>
                      <Button
                        type="primary"
                        className="filter-icon-btn"
                        icon={<FilterOutlined />}
                        onClick={() => setFilterVisible((v) => !v)}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    </Badge>
                  </Popover>
                  <Dropdown
                    overlay={<Menu onClick={colVisibilityClickHandler} items={columnItems} />}
                    placement="bottomRight"
                  >
                    <Button className="column-visibility">Column Visibility</Button>
                  </Dropdown>
                </>
              )}
              {addCustomerButton}
              {uploadButton}
            </div>
          </div>
        </div>
        <div className="list-page-table-outer">
          <CustomTable
          list={list}
          total={total}
          status={status}
          columnsToShow={columnsToShow}
          setColumnsToShow={setColumnsToShow}
          colVisibilityClickHandler={colVisibilityClickHandler}
          columnItems={columnItems}
          isSearching={isSearching}
          filteredList={filteredList}
          resolvePartyBalance={(record) =>
            getPartyListBalance(record, summaryDueByCustomerIdRef.current)
          }
        />
        </div>
      </div>
    </div>
  );
};

export default GetAllCust;